import React, { FC, isValidElement, ReactNode, useMemo } from 'react';
import { Campaign } from '@shared/types/mylounge';
import { containerClass, gridWrapperClass } from '../styles';
import {
    CampaignBlock,
    CampaignSlotMarker,
    SlotSize,
    isInsertable,
    type SlotItem,
} from './CampaignBlock';
import { FillRemaining } from './FillRemaining';
import { ShowstopperBlock } from './ShowstopperBlock';
import { useGridLayoutContext } from '../context/GridLayoutContext';
import type { GridLayoutContextValue } from '../context/GridLayoutContext';

interface DeclarativeGridProps {
    children: ReactNode;
    afterFillRemaining?: ReactNode;
    fillRemainingSlot?: SlotSize;
    customCampaignsList?: Campaign[];
}

/**
 * Calculates how many campaign slots a child component consumes.
 *
 *
 * Rules:
 * - CampaignBlock: returns count of slots that consume campaigns (excluding CUSTOM_CONTENT)
 * - FillRemaining: returns rowCount * columnCount (grid cells to fill)
 * - ShowstopperBlock: returns fallbackLayout.length (only if wrapping CampaignBlock)
 * - Raw components (Carousel, etc.): returns 0 (no campaign slots)
 *
 * @param child - React element to calculate slots for
 * @param columnCount - Number of columns in the grid
 * @returns Number of campaign slots consumed by this child
 */
const calculateChildSlots = (child: React.ReactElement, columnCount: number): number => {
    if (!isValidElement(child)) {
        return 0;
    }

    // CampaignBlock: count only slots that consume campaigns (exclude CUSTOM_CONTENT)
    if (child.type === CampaignBlock) {
        const campaignBlockChild = child as React.ReactElement<{ slots?: SlotItem[] }>;
        const slots = campaignBlockChild.props.slots ?? [];

        return slots.filter(slot => {
            // If it's an Insertable with CUSTOM_CONTENT, don't count it
            if (isInsertable(slot) && slot.campaignIndex === CampaignSlotMarker.CUSTOM_CONTENT) {
                return false;
            }
            // Otherwise, count it
            return true;
        }).length;
    }

    // FillRemaining: count rowCount * columnCount
    if (child.type === FillRemaining) {
        const fillRemainingChild = child as React.ReactElement<{ rowCount?: number }>;
        return (fillRemainingChild.props.rowCount ?? 1) * columnCount;
    }

    // ShowstopperBlock: count fallbackLayout (if condition is false, fallback renders)
    if (child.type === ShowstopperBlock) {
        const showstopperChild = child as React.ReactElement<{
            condition?: boolean;
            fallbackLayout?: SlotSize[];
        }>;
        // Only count if condition is false (fallback layout will be shown)
        if (!showstopperChild.props.condition) {
            return showstopperChild.props.fallbackLayout?.length ?? 1;
        }
        // If condition is true, the wrapped component doesn't consume campaigns
        return 0;
    }

    // Raw components (carousels, etc.): 0 slots
    return 0;
};

/**
 * DeclarativeGrid is the core layout engine that processes React children and injects
 * campaign offsets into CampaignBlock and FillRemaining components.
 *
 * Opzione 3 Implementation:
 * 1. Traverse React children tree
 * 2. Calculate cumulative offset for each CampaignBlock/FillRemaining
 * 3. Inject startIndex prop into these components
 * 4. Wrap raw components in a full-width <li> (grid-spanning)
 * 5. Render FillRemaining at the end with the final offset
 *
 * Key Benefits:
 * - ✅ No shared mutable state (ref eliminated from context)
 * - ✅ SSR-safe (index derived, not mutated)
 * - ✅ Explicit campaign positioning
 * - ✅ Constraint: Showstoppers after missing ones never rise in position
 */
export const DeclarativeGrid: FC<DeclarativeGridProps> = ({
    children,
    afterFillRemaining,
    fillRemainingSlot,
    customCampaignsList: campaignsToShow,
}) => {
    const context = useGridLayoutContext() as GridLayoutContextValue & {
        _resetCampaignIndex: () => void;
    };
    const { columnCount, _resetCampaignIndex } = context;

    const processedChildren = useMemo(() => {
        // Reset offset calculation at the start of each render
        _resetCampaignIndex();

        let currentOffset = 0;
        let childIndex = 0;
        const result: React.ReactNode[] = [];

        React.Children.forEach(children, child => {
            if (!isValidElement(child)) {
                return;
            }

            // Calculate slots for this child
            const slotsConsumed = calculateChildSlots(child as React.ReactElement, columnCount);

            // For CampaignBlock: render with startIndex prop
            if (child.type === CampaignBlock) {
                const props = { ...child.props, startIndex: currentOffset };
                result.push(
                    React.createElement(CampaignBlock, {
                        key: `campaign-block-${childIndex}`,
                        ...props,
                    }),
                );
                currentOffset += slotsConsumed;
                childIndex++;
                return;
            }

            // For FillRemaining: render with startIndex prop
            if (child.type === FillRemaining) {
                const props = { ...child.props, startIndex: currentOffset };
                result.push(
                    React.createElement(FillRemaining, {
                        key: `fill-remaining-${childIndex}`,
                        ...props,
                    }),
                );
                currentOffset += slotsConsumed;
                childIndex++;
                return;
            }

            // For ShowstopperBlock: check if fallback will render
            if (child.type === ShowstopperBlock) {
                // If fallback layout will render, it consumes slots
                if (!(child.props as Record<string, unknown>).condition) {
                    currentOffset += slotsConsumed;
                }
                result.push(
                    React.createElement(
                        'li',
                        {
                            key: `showstopper-block-${childIndex}`,
                            className: 'relative h-full overflow-hidden w-full col-[1_/_-1]',
                        },
                        child,
                    ),
                );
                childIndex++;
                return;
            }

            // Raw components (carousels, etc.): wrap and don't affect offset
            result.push(
                React.createElement(
                    'li',
                    {
                        key: `raw-component-${childIndex}`,
                        className: 'relative h-full overflow-hidden w-full col-[1_/_-1]',
                    },
                    child,
                ),
            );
            childIndex++;
        });

        return result;
    }, [children, columnCount, _resetCampaignIndex]);

    // Calculate FillRemaining offset based on all processed children
    const fillRemainingOffset = useMemo(() => {
        let offset = 0;
        React.Children.forEach(children, child => {
            if (isValidElement(child)) {
                offset += calculateChildSlots(
                    child as React.ReactElement<Record<string, unknown>>,
                    columnCount,
                );
            }
        });
        return offset;
    }, [children, columnCount]);

    return (
        <div className={containerClass}>
            <ul {...gridWrapperClass(columnCount)} id="campaigns-open">
                {/* Render processed children with injected startIndex */}
                {processedChildren}
                <FillRemaining
                    slot={fillRemainingSlot}
                    customCampaignsList={campaignsToShow}
                    startIndex={fillRemainingOffset}
                />
                {afterFillRemaining}
            </ul>
        </div>
    );
};
